/**
 * Group Finder Widget - Embeddable Bundle Entry Point
 *
 * Replaces the MP CustomWidget embed on woodsidebible.org/group-finder/.
 * Renders inside a shadow root for style isolation from the WordPress theme,
 * following the whats-happening widget pattern (see shell.tsx).
 *
 * Embed:
 *   <div id="group-finder-root" data-params="campus=troy"></div>
 *   <script src="https://widgets.woodsidebible.org/group-finder/widget.js"></script>
 *
 * The old embed's mount id (`groupFinder`) is accepted as a fallback so the
 * page's existing `#groupFinder` anchor links keep working.
 *
 * The sheets portal into a SECOND shadow root that hangs off document.body,
 * not into the widget's own. On woodsidebible.org the widget sits inside
 * .container-wrap and a WPBakery column, both `position: relative;
 * z-index: 10`, so a fixed overlay rendered in the widget's root is trapped
 * in that stacking context and the theme's fixed header (z-index 9999) and
 * page title band (z-index 1000) paint over the backdrop and the modal.
 */

import React from 'react';
import { GroupFinder } from '../src/components/GroupFinder';
import { attr, parseDataParams } from './params';
import { ShadowWidget } from './shell';
import { DEFAULT_ASSET_BASE, setSeeJesusAssetBase } from '../src/lib/seeJesus';

interface WidgetConfig {
  apiBaseUrl?: string;
  /** Where the widget's public assets live (default: widgets-host) */
  assetBaseUrl?: string;
  containerId?: string;
  detailsUrl?: string;
  inquiryFormUrl?: string;
}

declare global {
  interface Window {
    GROUP_FINDER_WIDGET_CONFIG?: WidgetConfig;
    GroupFinderWidget?: ShadowWidget;
  }
}

const DEFAULT_CONTAINER_ID = 'group-finder-root';
const LEGACY_CONTAINER_ID = 'groupFinder';

function createFinderWidget(config: WidgetConfig): ShadowWidget {
  const apiBaseUrl = config.apiBaseUrl || 'https://api.woodsidebible.org';
  const assetBaseUrl = (config.assetBaseUrl || DEFAULT_ASSET_BASE).replace(/\/$/, '');
  setSeeJesusAssetBase(assetBaseUrl);
  return new ShadowWidget({
    assetBaseUrl,
    containerIds: config.containerId
      ? [config.containerId]
      : [DEFAULT_CONTAINER_ID, LEGACY_CONTAINER_ID],
    overlayHostId: 'group-finder-overlay',
    fontId: 'group-finder-widget-montserrat',
    scrollLockFlag: '__GROUP_FINDER_WIDGET_SCROLL_LOCK_PREVENTED__',
    label: 'Group Finder Widget',
    render: (container) => {
      // Precedence: explicit config object > dedicated data-* attribute > the
      // shared data-params string.
      const embedParams = parseDataParams(container.getAttribute('data-params'));
      const campusAttr = attr(container.dataset.campus);
      if (campusAttr) embedParams.push(['campus', campusAttr]);
      return (
        <GroupFinder
          apiBaseUrl={apiBaseUrl}
          embedParams={embedParams}
          detailsUrl={config.detailsUrl ?? attr(container.dataset.detailsUrl)}
          inquiryFormUrl={config.inquiryFormUrl ?? attr(container.dataset.inquiryUrl)}
        />
      );
    },
  });
}

// Auto-initialize
if (typeof window !== 'undefined') {
  // API base is the centralized api.woodsidebible.org host, baked at build
  // time from CHURCH_HUB_API_URL. (Do NOT derive it from the script origin:
  // the script is served from widgets.woodsidebible.org, which has no /api
  // routes.) window.GROUP_FINDER_WIDGET_CONFIG can override for local testing.
  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    ...(window.GROUP_FINDER_WIDGET_CONFIG || {}),
  };
  window.GROUP_FINDER_WIDGET_CONFIG = config;

  // Skip if already initialized (e.g. script loaded twice via bfcache)
  if (!window.GroupFinderWidget) {
    const boot = () => {
      const widget = createFinderWidget(config);
      widget.init();
      window.GroupFinderWidget = widget;
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

export default ShadowWidget;
